/**
 * Shared, premium-feeling style for MUI `Menu` popovers and every `Select`
 * dropdown across the portal. Tight padding, small icons, subtle border,
 * soft shadow. Import + spread into `slotProps` (menus) or `MenuProps`
 * (Selects).
 *
 *   <Menu {...elegantMenuProps} ...>
 *   <Select MenuProps={elegantMenuProps} ...>
 */
export const elegantMenuProps = {
  slotProps: {
    paper: {
      elevation: 0,
      sx: {
        minWidth: 180,
        mt: 0.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: (theme: any) =>
          theme.palette.mode === 'dark'
            ? '0 8px 24px rgba(0, 0, 0, 0.45)'
            : '0 8px 24px rgba(15, 23, 42, 0.08)',
        overflow: 'hidden',
        '& .MuiMenuItem-root': {
          minHeight: 34,
          py: 0.75,
          px: 1.5,
          fontSize: '0.825rem',
          fontWeight: 500,
          gap: 1,
          transition: 'background-color 80ms ease',
        },
        '& .MuiListItemIcon-root': {
          minWidth: 28,
          color: 'text.secondary',
          '& .MuiSvgIcon-root': { fontSize: 18 },
        },
        '& .MuiListItemText-root .MuiTypography-root': {
          fontSize: '0.825rem',
          fontWeight: 500,
        },
        '& .MuiDivider-root': { my: 0.5 },
      },
    },
  },
  MenuListProps: { dense: true, sx: { py: 0.5 } },
} as const;

/**
 * `elegantMenuProps` for action menus whose items carry a one-line hint
 * under the label (`<ListItemText primary=… secondary=… />`) — e.g. the
 * admissions "Forms & links" and per-application actions menus.
 */
export const elegantMenuWithHintsProps = {
  ...elegantMenuProps,
  slotProps: {
    paper: {
      ...elegantMenuProps.slotProps.paper,
      sx: {
        ...elegantMenuProps.slotProps.paper.sx,
        minWidth: 260,
        '& .MuiMenuItem-root': {
          ...elegantMenuProps.slotProps.paper.sx['& .MuiMenuItem-root'],
          py: 1,
          alignItems: 'flex-start',
        },
        '& .MuiListItemIcon-root': {
          ...elegantMenuProps.slotProps.paper.sx['& .MuiListItemIcon-root'],
          mt: 0.25,
        },
        '& .MuiListItemText-root': { my: 0 },
        '& .MuiListItemText-root .MuiListItemText-secondary': {
          fontSize: '0.72rem',
          fontWeight: 400,
          color: 'text.secondary',
          mt: 0.25,
        },
      },
    },
  },
} as const;
